import { BrandOptions } from "@/app/(auth)/select-brand/components/BrandAction/type";
import { Asset, Pagination, Project, SavedWorkData } from "@/app/(protected)/saved-projects/types";
import { DEFAULT_LOADER_CONTENT } from "@/components/Loader/constants";
import { LoaderContent } from "@/components/Loader/type";

export interface State {
  isOpen: boolean;
  isModalOpen: boolean;
  isFormUnsaved: boolean;
  pendingRoute: string | null;
  isLoading: boolean;
  isNewProjectSaved: boolean;
  savedWorkData: SavedWorkData;
  brandToBeSelected?: BrandOptions | null;
  isTimeoutModalOpen?: boolean;
  currentPageName?: string;
  refinementInProgress?: boolean;
  imageGenErrorModalOpen?: boolean;
  currentUserflow?: string;
  currentStepName?: string;
  ref?: React.RefObject<HTMLElement | null> | null;
  customModalContent?: LoaderContent;
  isTypeImageAsset?: boolean;
  isFullyGeneratedImage?: boolean;
  cancelTrigger?: boolean;
  linkText?: string;
}

const initialPagination: Pagination = {
  totalItems: 0,
  totalPages: 0,
  currentPage: 0,
  pageSize: 0,
};

export const initialState: State = {
  isOpen: false,
  isModalOpen: false,
  isFormUnsaved: false,
  pendingRoute: null,
  isLoading: false,
  brandToBeSelected: null,
  isTimeoutModalOpen: false,
  currentPageName: "",
  refinementInProgress: false,
  imageGenErrorModalOpen: false,
  currentUserflow: "",
  currentStepName: "",
  ref: null,
  isNewProjectSaved: false,
  savedWorkData: {
    projects: [],
    savedAssets: [],
    archive: [],
    totalProjects: 0,
    pagination: {
      projects: initialPagination,
      assets: initialPagination,
      archive: initialPagination,
    },
    allSavedProjectsData: [],
  },
  customModalContent: DEFAULT_LOADER_CONTENT,
  isTypeImageAsset: false,
  isFullyGeneratedImage: false,
  cancelTrigger: false,
  linkText: "",
};

export type Actions =
  | { type: "TOGGLE_SIDEBAR"; payload: boolean }
  | { type: "TOGGLE_MODAL"; payload: boolean }
  | { type: "SET_FORM_UNSAVED"; payload: boolean }
  | { type: "SET_PENDING_ROUTE"; payload: string | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "RESET" }
  | { type: "SET_IS_NEW_PROJECT_SAVED"; payload: boolean }
  | { type: "SET_SAVED_WORK"; payload: SavedWorkData }
  | {
      type: "UPDATE_SAVED_PROJECTS";
      payload: {
        projects: Project[];
        assets: Asset[];
        archive: Project[];
        pagination: {
          projects: Pagination;
          assets: Pagination;
          archive: Pagination;
        };
      };
    }
  | {
      type: "SET_TOTAL_PROJECTS";
      payload: number;
    }
  | { type: "RESET_REFERENCE_VALUE" }
  | { type: "RESET_SAVED_WORK" }
  | { type: "SET_BRAND_TOGGLE_MODAL"; payload: { open: boolean; brand: BrandOptions | null } }
  | { type: "TOGGLE_TIMEOUT_MODAL"; payload: boolean; ref?: React.RefObject<HTMLElement | null> }
  | { type: "SET_CURRENT_PAGE_NAME"; payload?: string }
  | { type: "SET_PROJECT_REFINEMENT"; payload?: boolean }
  | { type: "TOGGLE_IMAGE_GEN_ERROR"; payload?: boolean }
  | { type: "SET_CURRENT_USER_FLOW"; payload?: string }
  | { type: "SET_CURRENT_STEP_NAME"; payload?: string }
  | { type: "SET_CUSTOM_MODAL_CONTENT"; payload?: LoaderContent }
  | { type: "RESET_CUSTOM_MODAL_CONTENT"; payload?: LoaderContent }
  | { type: "IS_IMAGE_TYPE_ASSET"; payload?: boolean }
  | { type: "IS_FULLY_GENERATED_IMAGE"; payload?: boolean }
  | { type: "SET_CANCEL_TRIGGER"; payload?: boolean }
  | { type: "SET_LINK_TEXT"; payload?: string };

export const reducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case "TOGGLE_SIDEBAR":
      return { ...state, isOpen: action.payload };
    case "TOGGLE_MODAL":
      return { ...state, isModalOpen: action.payload };
    case "SET_FORM_UNSAVED":
      return { ...state, isFormUnsaved: action.payload };
    case "SET_PENDING_ROUTE":
      return { ...state, pendingRoute: action.payload };
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
        customModalContent: action.payload === false ? DEFAULT_LOADER_CONTENT : state.customModalContent,
      };

    case "RESET":
      return initialState;
    case "SET_IS_NEW_PROJECT_SAVED":
      return { ...state, isNewProjectSaved: action.payload };
    case "SET_SAVED_WORK":
      return { ...state, savedWorkData: action.payload };
    case "UPDATE_SAVED_PROJECTS":
      return {
        ...state,
        savedWorkData: {
          ...state.savedWorkData,
          projects: action.payload.projects,
          savedAssets: action.payload.assets,
          archive: action.payload.archive,
          pagination: {
            projects: action.payload.pagination.projects,
            assets: action.payload.pagination.assets,
            archive: action.payload.pagination.archive,
          },
        },
      };
    case "SET_TOTAL_PROJECTS":
      return {
        ...state,
        savedWorkData: {
          ...state.savedWorkData,
          totalProjects: action.payload,
        },
      };
    case "SET_BRAND_TOGGLE_MODAL":
      return {
        ...state,
        isModalOpen: action.payload.open,
        brandToBeSelected: action.payload.brand,
      };
    case "TOGGLE_TIMEOUT_MODAL":
      return { ...state, isTimeoutModalOpen: action.payload, ref: action.ref };
    case "SET_CURRENT_PAGE_NAME":
      return { ...state, currentPageName: action.payload };
    case "SET_PROJECT_REFINEMENT":
      return { ...state, refinementInProgress: action.payload };
    case "TOGGLE_IMAGE_GEN_ERROR":
      return { ...state, imageGenErrorModalOpen: action.payload };
    case "SET_CURRENT_USER_FLOW":
      return { ...state, currentUserflow: action.payload };
    case "SET_CURRENT_STEP_NAME":
      return { ...state, currentStepName: action.payload };
    case "SET_CUSTOM_MODAL_CONTENT":
      return { ...state, customModalContent: action.payload };
    case "RESET_CUSTOM_MODAL_CONTENT":
      return { ...state, customModalContent: DEFAULT_LOADER_CONTENT };
    case "RESET_REFERENCE_VALUE":
      return { ...state, ref: null };
    case "IS_IMAGE_TYPE_ASSET":
      return { ...state, isTypeImageAsset: action.payload };
    case "IS_FULLY_GENERATED_IMAGE":
      return { ...state, isFullyGeneratedImage: action.payload };
    case "SET_CANCEL_TRIGGER":
      return { ...state, cancelTrigger: action.payload };
    case "SET_LINK_TEXT":
      return { ...state, linkText: action.payload };
    default:
      return state;
  }
};
